import OrderUtils from 'utils/Order';
import basketUtils from 'utils/Basket';
import checkoutApi from 'services/api/checkout';
import checkoutUtils from 'utils/Checkout';
import LoadScripts from 'utils/LoadScripts';
import localeUtils from 'utils/LanguageLocale';
import store from 'Store';
import Actions from 'Actions';
import userUtils from 'utils/User';
import * as GooglePayHandlers from 'utils/GooglePayHandlers';
import GooglePayEN from 'utils/locales/GooglePay_en_US';
import GooglePayFR from 'utils/locales/GooglePay_fr_CA';
import locationUtils from 'utils/Location';
import { PLACEHOLDER_SHIPPING_OPTION_ID } from 'constants/GooglePay';
import { clearPendingInit, getPendingInit, setPendingInit } from 'utils/googlePayPendingInit';
import { isGuestCheckoutEnabledSelector } from 'viewModel/selectors/guestCheckout/isGuestCheckoutEnabledSelector';
import { isGuestCheckoutEnabledForBopisSelector } from 'viewModel/selectors/guestCheckout/isGuestCheckoutEnabledForBopisSelector';
import { showGooglePayAbTestSelector } from 'viewModel/selectors/testTarget/showGooglePayAbTestSelector';

// Required by the official Google Pay spec
// (https://developers.google.com/pay/api/web/guides/tutorial)
const BASE_REQUEST = {
    apiVersion: 2,
    apiVersionMinor: 0
};

const ALLOWED_CARD_AUTH_METHODS = ['PAN_ONLY', 'CRYPTOGRAM_3DS'];

const ALLOWED_CARD_NETWORKS = ['VISA', 'MASTERCARD', 'AMEX', 'DISCOVER'];

const GOOGLE_PAY_ERROR_MESSAGES = {
    incompleteCheckout: 'incompleteCheckout'
};

const SDK_LOAD_TIMEOUT_MS = 10000;

const TYPES = {
    ENABLED: 'ENABLED',
    HIDDEN: 'HIDDEN'
};

// Google Pay API enums — values are dictated by the SDK contract.
// See https://developers.google.com/pay/api/web/reference/request-objects.
// (Country codes 'US'/'CA' reuse localeUtils.COUNTRIES rather than duplicating here.)
const PAYMENT_METHOD_TYPES = {
    CARD: 'CARD'
};

const TOKENIZATION_TYPES = {
    PAYMENT_GATEWAY: 'PAYMENT_GATEWAY'
};

const ADDRESS_FORMATS = {
    FULL: 'FULL'
};

const TOTAL_PRICE_STATUS = {
    ESTIMATED: 'ESTIMATED',
    FINAL: 'FINAL'
};

const CALLBACK_INTENTS = {
    PAYMENT_AUTHORIZATION: 'PAYMENT_AUTHORIZATION',
    SHIPPING_ADDRESS: 'SHIPPING_ADDRESS',
    SHIPPING_OPTION: 'SHIPPING_OPTION'
};

const SDK_STATUS_CODES = {
    CANCELED: 'CANCELED'
};

// Keys into utils/locales/GooglePay_{en_US,fr_CA}.js
const LOCALE_KEYS = {
    ESTIMATED_TOTAL: 'estimatedTotal',
    SHIPPING_OPTION_PLACEHOLDER_DESCRIPTION: 'shippingOptionPlaceholderDescription',
    SHIPPING_OPTION_PLACEHOLDER_LABEL: 'shippingOptionPlaceholderLabel',
    TOTAL: 'total'
};

// Holds data for the current checkout (order, shippingGroup, shippingMethods, etc) so future callbacks
// don't have to refetch it
let checkoutContext = null;

// Guards against re-entrant startCheckout calls (double-tap on the Google
// Pay button). The paysheet takes 2-3s to open while startCheckout runs its
// backend init awaits; the interstitial dispatched at entry reduces the
// incentive to re-tap but does not block the browser event itself — a user
// who still taps again would spawn a concurrent loadPaymentData whose
// PaymentRequest.show() arrives at Chrome after the click's transient user
// activation was consumed by the first flow's awaits, and Chrome rejects it
// with "require either transient user activation or delegated payment request
// capability". Silent no-op on re-entry is intentional: the first flow is
// still on its way to opening the paysheet.
let startCheckoutInFlight = false;
let openCheckoutPaysheetInFlight = false;

// BOPIS: SDK does not fire INITIALIZE, so backend init runs in the background;
// onBopisPaymentAuthorized awaits this before placeOrder.
let bopisBackgroundInitPromise = null;

// Placeholder-first flow stashes tap-time init params on the shared
// `utils/googlePayPendingInit` module — GooglePayHandlers picks them up
// inside the INITIALIZE callback. See that module for the "why" of the
// extraction (cyclic-import guard).

/**
 * The two-letter country codes Google Pay should accept for the shipping address.
 *
 * @returns {string[]}
 */
function getAllowedCountryCodes() {
    return [localeUtils.isCanada() ? localeUtils.COUNTRIES.CA : localeUtils.COUNTRIES.US];
}

/**
 * Maps the Cart Service available shipping methods to Google Pay's
 * `shippingOptionParameters`. Google's option has no amount field, so the fee
 * is appended to the description
 *
 * @param {Array} shippingMethods - from checkoutApi.getAvailableShippingMethods
 * @param {string|number} [currentShippingMethodId] - the order's current method, used as default
 * @returns {{ defaultSelectedOptionId: string, shippingOptions: Array<{id,label,description}> }}
 */
function getShippingOptions(shippingMethods = [], currentShippingMethodId) {
    const shippingOptions = shippingMethods.map(method => ({
        id: String(method.shippingMethodId),
        label: method.shippingMethodType,
        description: [method.shippingMethodDescription, checkoutUtils.setShippingFee(method.shippingFee)].filter(Boolean).join(' — ')
    }));

    const currentId = String(currentShippingMethodId);
    const defaultSelectedOptionId = shippingOptions.some(option => option.id === currentId) ? currentId : shippingOptions[0]?.id;

    return { defaultSelectedOptionId, shippingOptions };
}

/**
 * Returns Google Pay `shippingOptionParameters` with a single placeholder
 * option whose label/description are localized.
 *
 * The placeholder is required whenever the initial PaymentDataRequest opens
 * without a known method list (guest STH: no default address yet, so Cart
 * Service returns no methods). Sending an empty `shippingOptions` array with
 * `shippingOptionRequired: true` violates the Google Pay spec and the paysheet
 * refuses buyflow with a generic `OR_BIBED_06` error before onPaymentAuthorized
 * ever fires (PAY-7485). And omitting `shippingOptionParameters` entirely means
 * the paysheet never renders the shipping-method selector, because Google's
 * dynamic callbacks (`onPaymentDataChanged`) can only REPLACE an existing
 * `shippingOptionParameters` set, not create one (PAY-7688).
 *
 * A one-item placeholder threads both needles: the array is non-empty (spec
 * satisfied), and the selector renders inline. Once the customer supplies an
 * address the INITIALIZE / SHIPPING_ADDRESS callback returns
 * `newShippingOptionParameters` with real cart methods and Google swaps them
 * in.
 */
function getShippingOptionPlaceholderParameters() {
    const getText = localeUtils.getLocalisedDataFromResource(GooglePayEN, GooglePayFR);

    return {
        defaultSelectedOptionId: PLACEHOLDER_SHIPPING_OPTION_ID,
        shippingOptions: [
            {
                id: PLACEHOLDER_SHIPPING_OPTION_ID,
                label: getText(LOCALE_KEYS.SHIPPING_OPTION_PLACEHOLDER_LABEL),
                description: getText(LOCALE_KEYS.SHIPPING_OPTION_PLACEHOLDER_DESCRIPTION)
            }
        ]
    };
}

function getShippingOptionParametersOrPlaceholder(shippingMethods, currentShippingMethodId) {
    if (Array.isArray(shippingMethods) && shippingMethods.length > 0) {
        return getShippingOptions(shippingMethods, currentShippingMethodId);
    }

    return getShippingOptionPlaceholderParameters();
}

function getBaseCardPaymentMethod() {
    return {
        type: PAYMENT_METHOD_TYPES.CARD,
        parameters: {
            allowedAuthMethods: ALLOWED_CARD_AUTH_METHODS,
            allowedCardNetworks: ALLOWED_CARD_NETWORKS
        }
    };
}

function getTokenizationSpecification() {
    const { googlePayGateway, gatewayMerchantIdUS, gatewayMerchantIdCA } = Sephora.configurationSettings.googlePayConfig || {};
    const gatewayMerchantId = localeUtils.isCanada() ? gatewayMerchantIdCA : gatewayMerchantIdUS;

    return {
        type: TOKENIZATION_TYPES.PAYMENT_GATEWAY,
        parameters: {
            gateway: googlePayGateway,
            gatewayMerchantId
        }
    };
}

function getCardPaymentMethod() {
    const baseCardPaymentMethod = getBaseCardPaymentMethod();

    return {
        ...baseCardPaymentMethod,
        parameters: {
            ...baseCardPaymentMethod.parameters,
            // Returns the billing address tied to the selected card
            billingAddressRequired: true,
            billingAddressParameters: {
                format: ADDRESS_FORMATS.FULL,
                phoneNumberRequired: true
            }
        },
        tokenizationSpecification: getTokenizationSpecification()
    };
}

function getIsReadyToPayRequest() {
    return {
        ...BASE_REQUEST,
        allowedPaymentMethods: [getBaseCardPaymentMethod()]
    };
}

/**
 * The `allowedPaymentMethods` array to hand to the SDK `createButton` helper.
 * Google requires this to match the array passed to `loadPaymentData` (the
 * cart payment method, with tokenizationSpecification) so the SDK can size and
 * paint the signed-in "dynamic" (card-art) button correctly instead of letting
 * it grow past the container and overlap the adjacent express buttons.
 *
 * @returns {Array<Object>}
 */
function getAllowedPaymentMethodsForButton() {
    return [getCardPaymentMethod()];
}

function getMerchantInfo() {
    const { merchantId, merchantName } = Sephora.configurationSettings.googlePayConfig || {};

    return {
        merchantId,
        merchantName
    };
}

/**
 * Resolves the paysheet total the customer will actually be authorized for —
 * ceiling first (BOPIS item substitution can push the actual charge above the
 * displayed order total), then the credit-card residual after store credit /
 * gift cards are applied, then the tax/fee-inclusive `orderTotal`, then the
 * merchandise subtotal the button passed as a last resort.
 *
 * Priority order:
 *   1. priceInfo.maxAmountToBeAuthorized — BE-computed authorization ceiling
 *      (populated for BOPIS orders that allow item substitution; the payment
 *      auth must reserve this much even though the visible order total is lower).
 *   2. priceInfo.creditCardAmount — residual charged to the card after store
 *      credit / gift cards are deducted (same field ApplePay resolves with).
 *   3. priceInfo.orderTotal — tax/fee-inclusive final total. Same field the
 *      mid-paysheet recalc (GooglePayHandlers.buildTransactionInfo) uses, so
 *      the initial total and any post-address recalc stay consistent.
 *   4. fallbackAmount — merchandise subtotal from the button (pre-tax).
 *
 * Always returns a normalized 2-decimal money string (Google Pay rejects
 * non-numeric/non-string totalPrice), ending at '0.00' when no source yields
 * a usable non-negative number.
 *
 * @param {Object} priceInfo - from getOrderDetails
 * @param {string|number} fallbackAmount - basket subtotal passed from the button
 * @returns {string} 2-decimal money string (e.g. '125.50')
 */
function resolveTransactionAmount(priceInfo, fallbackAmount) {
    // isFinite (not !isNaN) also rejects Infinity/-Infinity, matching buildTransactionInfo.
    // allowZero honors a legitimately free order (orderTotal 0.00 after discounts),
    // which must win over a positive subtotal; the button subtotal fallback stays
    // strictly positive since a 0 subtotal carries no useful amount.
    const toMoneyString = (amount, { allowZero = false } = {}) => {
        // Absent/empty must fall through (a real 0 is indistinguishable from '' once
        // removeCurrency normalizes it), so only a present value can count as free.
        if (amount == null || String(amount).trim() === '') {
            return null;
        }

        const numericAmount = Number(basketUtils.removeCurrency(String(amount)));

        if (!Number.isFinite(numericAmount) || numericAmount < 0) {
            return null;
        }

        return numericAmount > 0 || allowZero ? numericAmount.toFixed(2) : null;
    };

    return (
        toMoneyString(priceInfo?.maxAmountToBeAuthorized) ||
        toMoneyString(priceInfo?.creditCardAmount, { allowZero: true }) ||
        toMoneyString(priceInfo?.orderTotal, { allowZero: true }) ||
        toMoneyString(fallbackAmount) ||
        '0.00'
    );
}

/**
 * Picks the tap-time placeholder total for the BOPIS paysheet.
 *
 * BOPIS has no INITIALIZE callback, so whatever we pass here is what the
 * customer sees. Priority:
 *   1. `priceInfo.creditCardAmount` from a prior checkout visit still in Redux —
 *      only trusted when `priceInfo.orderTotal` matches the current basket
 *      `estimatedTotal` (basket was not mutated after that visit).
 *   2. `estimatedTotal - storeCreditAmount - giftCardAmount` from the basket
 *      response, covering post-refresh and direct-load flows once the BE
 *      exposes `giftCardAmount` on the BOPIS response.
 *   3. Raw `estimatedTotal` when neither credit is applied.
 *
 * Returns null when even the estimated total is unusable so callers can fall
 * back to the merchandise subtotal.
 */
function resolveBopisBasketTransactionAmount({ estimatedTotal, storeCreditAmount, giftCardAmount, priceInfo }) {
    const parse = amount => {
        if (amount == null || String(amount).trim() === '') {
            return null;
        }

        const numeric = Number(basketUtils.removeCurrency(String(amount)));

        return Number.isFinite(numeric) ? numeric : null;
    };

    const est = parse(estimatedTotal);

    if (est == null || est < 0) {
        return null;
    }

    const reduxOrderTotal = parse(priceInfo?.orderTotal);
    const reduxCreditCard = parse(priceInfo?.creditCardAmount);

    if (reduxOrderTotal != null && reduxOrderTotal === est && reduxCreditCard != null && reduxCreditCard >= 0) {
        return reduxCreditCard.toFixed(2);
    }

    const sc = parse(storeCreditAmount) ?? 0;
    const gc = parse(giftCardAmount) ?? 0;

    return Math.max(0, est - sc - gc).toFixed(2);
}

function getTransactionInfo({ transactionAmount, transactionCurrencyCode }) {
    // Resolve EN/FR at call time (not module load) so the label follows the
    // customer's current locale — same pattern as utils/Checkout setShippingFee.
    const getText = localeUtils.getLocalisedDataFromResource(GooglePayEN, GooglePayFR);

    return {
        countryCode: localeUtils.isCanada() ? localeUtils.COUNTRIES.CA : localeUtils.COUNTRIES.US,
        currencyCode: transactionCurrencyCode,
        totalPriceStatus: TOTAL_PRICE_STATUS.ESTIMATED,
        totalPrice: String(transactionAmount),
        totalPriceLabel: getText(LOCALE_KEYS.ESTIMATED_TOTAL)
    };
}

function getSthPaymentDataRequest({ transactionAmount, transactionCurrencyCode, shippingMethods, currentShippingMethodId }) {
    // Always emit `shippingOptionRequired` + `shippingOptionParameters` +
    // SHIPPING_OPTION callback intent, even when Cart Service returned no
    // methods yet. `onPaymentDataChanged` can only REPLACE an existing
    // shippingOptionParameters set, not create one — so if the initial payload
    // omits them the paysheet never renders the shipping-method selector even
    // after the SHIPPING_ADDRESS callback returns real methods (PAY-7688). When
    // no methods are known upfront, `getShippingOptionParametersOrPlaceholder`
    // seeds a one-item placeholder that satisfies the spec (non-empty array,
    // so no OR_BIBED_06 from PAY-7485) and gets swapped for real methods by the
    // INITIALIZE / SHIPPING_ADDRESS callback.
    return {
        ...BASE_REQUEST,
        allowedPaymentMethods: [getCardPaymentMethod()],
        transactionInfo: getTransactionInfo({ transactionAmount, transactionCurrencyCode }),
        merchantInfo: getMerchantInfo(),
        emailRequired: true,
        shippingAddressRequired: true,
        shippingAddressParameters: {
            phoneNumberRequired: true,
            allowedCountryCodes: getAllowedCountryCodes()
        },
        shippingOptionRequired: true,
        shippingOptionParameters: getShippingOptionParametersOrPlaceholder(shippingMethods, currentShippingMethodId),
        callbackIntents: [CALLBACK_INTENTS.SHIPPING_ADDRESS, CALLBACK_INTENTS.SHIPPING_OPTION, CALLBACK_INTENTS.PAYMENT_AUTHORIZATION]
    };
}

/**
 * Builds the PaymentDataRequest for the standard checkout paysheet. On checkout
 * the shipping address and method are already configured upstream, so the
 * paysheet only collects the payment token and billing address.
 */
function getCheckoutPaymentDataRequest({ transactionAmount, transactionCurrencyCode }) {
    const getText = localeUtils.getLocalisedDataFromResource(GooglePayEN, GooglePayFR);

    return {
        ...BASE_REQUEST,
        allowedPaymentMethods: [getCardPaymentMethod()],
        transactionInfo: {
            ...getTransactionInfo({ transactionAmount, transactionCurrencyCode }),
            totalPriceStatus: TOTAL_PRICE_STATUS.FINAL,
            totalPriceLabel: getText(LOCALE_KEYS.TOTAL)
        },
        merchantInfo: getMerchantInfo(),
        emailRequired: true,
        callbackIntents: [CALLBACK_INTENTS.PAYMENT_AUTHORIZATION]
    };
}

function getBopisPaymentDataRequest({ transactionAmount, transactionCurrencyCode }) {
    return {
        ...BASE_REQUEST,
        allowedPaymentMethods: [getCardPaymentMethod()],
        transactionInfo: getTransactionInfo({ transactionAmount, transactionCurrencyCode }),
        merchantInfo: getMerchantInfo(),
        emailRequired: true,
        // No shipping fields (pickup is preset), no onPaymentDataChanged
        // callback wired. Caller must pass `estimatedTotal` from basket-read
        // as the placeholder — no callback can swap the paysheet total.
        callbackIntents: [CALLBACK_INTENTS.PAYMENT_AUTHORIZATION]
    };
}

function getSddPaymentDataRequest({ transactionAmount, transactionCurrencyCode }) {
    return {
        ...BASE_REQUEST,
        allowedPaymentMethods: [getCardPaymentMethod()],
        transactionInfo: getTransactionInfo({ transactionAmount, transactionCurrencyCode }),
        merchantInfo: getMerchantInfo(),
        emailRequired: true,
        shippingAddressRequired: true,
        shippingAddressParameters: {
            phoneNumberRequired: true,
            allowedCountryCodes: getAllowedCountryCodes()
        },
        // SDD / Mixed paysheet has no shipping-method selector
        shippingOptionRequired: false,
        callbackIntents: [CALLBACK_INTENTS.SHIPPING_ADDRESS, CALLBACK_INTENTS.PAYMENT_AUTHORIZATION]
    };
}

// Placeholder request so `loadPaymentData` can be called synchronously in the
// tap. STH/SDD swap to the real total via INITIALIZE; BOPIS never fires
// INITIALIZE, so the caller must pass a close-to-final total (basket-read
// `estimatedTotal` for BOPIS).
function buildPlaceholderPaymentDataRequest({ transactionAmount, transactionCurrencyCode, isBopis, isSdd }) {
    const placeholderAmount = transactionAmount ?? '0.00';

    if (isBopis) {
        return getBopisPaymentDataRequest({ transactionAmount: placeholderAmount, transactionCurrencyCode });
    }

    if (isSdd) {
        return getSddPaymentDataRequest({ transactionAmount: placeholderAmount, transactionCurrencyCode });
    }

    return getSthPaymentDataRequest({
        transactionAmount: placeholderAmount,
        transactionCurrencyCode,
        shippingMethods: [],
        currentShippingMethodId: null
    });
}

/**
 * Selects the cart-type-specific PaymentDataRequest builder. Shared by the
 * express init path and the reopen-after-close fast path so both emit an
 * identical request for a given cart type.
 */
function buildExpressPaymentDataRequest({
    isBopis, isSdd, transactionAmount, transactionCurrencyCode, shippingMethods, currentShippingMethodId
}) {
    if (isBopis) {
        return getBopisPaymentDataRequest({ transactionAmount, transactionCurrencyCode });
    }

    if (isSdd) {
        return getSddPaymentDataRequest({ transactionAmount, transactionCurrencyCode });
    }

    return getSthPaymentDataRequest({ transactionAmount, transactionCurrencyCode, shippingMethods, currentShippingMethodId });
}

/**
 * Constructs a Google Pay PaymentsClient with the given callbacks. Centralizes the
 * SDK availability check and environment lookup shared across every PaymentsClient
 * variant — the three cart-type express clients (STH / BOPIS / SDD) and the
 * dedicated checkout client (`getCheckoutPaymentsClient`).
 *
 * @param {Object} paymentDataCallbacks - Google Pay paymentDataCallbacks dict
 * @returns {google.payments.api.PaymentsClient}
 * @throws {Error} when the SDK script has not been loaded yet.
 */
function createPaymentsClient(paymentDataCallbacks) {
    const PaymentsClient = window.google?.payments?.api?.PaymentsClient;

    if (!PaymentsClient) {
        throw new Error('Google Pay SDK not loaded');
    }

    const { googlePayEnvironment } = Sephora.configurationSettings.googlePayConfig || {};

    return new PaymentsClient({
        environment: googlePayEnvironment,
        paymentDataCallbacks
    });
}

const GooglePayUtils = {
    TYPES,
    GOOGLE_PAY_ERROR_MESSAGES,

    paymentsClient: null,

    /**
     * Exposes the `allowedPaymentMethods` array for the SDK `createButton`
     * helper (see getAllowedPaymentMethodsForButton). Passed by GooglePayButton
     * so the rendered button matches the loadPaymentData request.
     *
     * @returns {Array<Object>}
     */
    getAllowedPaymentMethodsForButton,

    /**
     * Whether Google Pay should be offered to the current user, based on the
     * locale-specific killswitches in `googlePayConfig`.
     *
     * @returns {boolean}
     */
    isGooglePayEnabled: () => {
        const { isGooglePayDotComEnabledUS, isGooglePayDotComEnabledCA } = Sephora.configurationSettings.googlePayConfig || {};

        if (localeUtils.isUS()) {
            return Boolean(isGooglePayDotComEnabledUS?.isEnabled);
        } else if (localeUtils.isCanada()) {
            return Boolean(isGooglePayDotComEnabledCA?.isEnabled);
        }

        return false;
    },

    /**
     * Returns Google Pay cart eligibility from Redux basket state via basketUtils;
     * checkout returns `false` until the order-payload helper lands (see TODO).
     *
     * @param {Object} [basket] - basket slice to read eligibility from. BOPIS passes
     *   the pickupBasket slice; omitted on checkout and for the root/DC basket.
     * @returns {boolean}
     */
    isGooglePayCartEligibleForCurrentPage: function (basket) {
        const isCheckout = locationUtils.isCheckout();

        if (isCheckout) {
            return OrderUtils.isGooglePayEnabledForThisOrder();
        }

        return basketUtils.isGooglePayCartEligible(basket);
    },

    resolveBopisBasketTransactionAmount,
    /**
     * Resolves the Google Pay button visibility state for the basket surface.
     * Returns HIDDEN when any gate fails (cheap → expensive): locale killswitch,
     * the Adobe Target A/B test gate, payment eligibility, basket surface enable,
     * cart eligibility, or the SDK `isReadyToPay` probe. Errors during SDK load or
     * the probe are treated as HIDDEN.
     *
     * Basket-only by name; checkout needs its own helper once the order-payload
     * eligibility check lands (TODO inside isGooglePayCartEligibleForCurrentPage).
     *
     * @param {Object} [basket] - basket slice to gate eligibility on. BOPIS passes
     *   the pickupBasket slice (its isGooglePayPaymentEligible flag lives there);
     *   omitted for the root/DC basket.
     * @returns {Promise<'ENABLED' | 'HIDDEN'>}
     */
    getGooglePaymentTypeForBasket: async function (basket) {
        if (!this.isGooglePayEnabled() || !showGooglePayAbTestSelector(store.getState())) {
            return TYPES.HIDDEN;
        }

        const { googlePayConfig } = Sephora.configurationSettings;
        const isBasketSurfaceEnabled = Boolean(googlePayConfig?.isGooglePayEnabledBasket?.isEnabled);
        const isCartEligible = this.isGooglePayCartEligibleForCurrentPage(basket);

        if (!isBasketSurfaceEnabled || !isCartEligible) {
            return TYPES.HIDDEN;
        }

        try {
            await this.initPaymentsClient();

            return TYPES.ENABLED;
        } catch (err) {
            return TYPES.HIDDEN;
        }
    },

    /**
     * Singleton for google.payments.api.PaymentsClient. Created on first call;
     * subsequent calls return the same instance.
     *
     * environment: 'TEST' | 'PRODUCTION' (official SDK values).
     *
     * @throws {Error} when the SDK script has not been loaded yet.
     * @returns {google.payments.api.PaymentsClient}
     */
    getPaymentsClient: function () {
        if (!this.paymentsClient) {
            this.paymentsClient = createPaymentsClient({
                onPaymentDataChanged: data => this.onPaymentDataChanged(data),
                onPaymentAuthorized: paymentData => this.onSthPaymentAuthorized(paymentData)
            });
        }

        return this.paymentsClient;
    },

    /**
     * Singleton for the BOPIS-only PaymentsClient. Only `onPaymentAuthorized`
     * is attached — BOPIS declares `callbackIntents: ['PAYMENT_AUTHORIZATION']`
     * and never fires the paysheet data-change callback. Backend init is kicked
     * off from `openPaysheetInitOnDemand` and awaited in `onBopisPaymentAuthorized`.
     *
     * @throws {Error} when the SDK script has not been loaded yet.
     * @returns {google.payments.api.PaymentsClient}
     */
    getBopisPaymentsClient: function () {
        if (!this.bopisPaymentsClient) {
            this.bopisPaymentsClient = createPaymentsClient({
                onPaymentAuthorized: paymentData => this.onBopisPaymentAuthorized(paymentData)
            });
        }

        return this.bopisPaymentsClient;
    },

    /**
     * Singleton for the SDD / Mixed PaymentsClient.
     *
     * Kept separate from `getPaymentsClient` (STH) so attaching
     * `onPaymentAuthorized` here does not force STH requests to declare
     * PAYMENT_AUTHORIZATION.
     *
     * @throws {Error} when the SDK script has not been loaded yet.
     * @returns {google.payments.api.PaymentsClient}
     */
    getSddPaymentsClient: function () {
        if (!this.sddPaymentsClient) {
            this.sddPaymentsClient = createPaymentsClient({
                onPaymentDataChanged: data => this.onPaymentDataChanged(data),
                onPaymentAuthorized: paymentData => this.onSddPaymentAuthorized(paymentData)
            });
        }

        return this.sddPaymentsClient;
    },

    /**
     * Selects the cart-type-specific express PaymentsClient singleton. Shared by
     * the express init path and the reopen-after-close fast path so both target
     * the same client for a given cart type.
     *
     * @param {Object} args
     * @param {boolean} [args.isBopis]
     * @param {boolean} [args.isSdd]
     * @returns {google.payments.api.PaymentsClient}
     */
    getExpressPaymentsClient: function ({ isBopis = false, isSdd = false } = {}) {
        if (isBopis) {
            return this.getBopisPaymentsClient();
        }

        if (isSdd) {
            return this.getSddPaymentsClient();
        }

        return this.getPaymentsClient();
    },

    /**
     * Singleton for the checkout PaymentsClient. Checkout collects payment +
     * billing only (shipping is already configured), so only
     * onPaymentAuthorized is registered — no onPaymentDataChanged.
     *
     * @throws {Error} when the SDK script has not been loaded yet.
     * @returns {google.payments.api.PaymentsClient}
     */
    getCheckoutPaymentsClient: function () {
        if (!this.checkoutPaymentsClient) {
            this.checkoutPaymentsClient = createPaymentsClient({
                onPaymentAuthorized: paymentData => this.onCheckoutPaymentAuthorized(paymentData)
            });
        }

        return this.checkoutPaymentsClient;
    },

    /**
     * Creates the Google Pay payment group on the backend (mirrors Paze's
     * initPaymentGroup). Does NOT fetch order details — call
     * `initGooglePaymentGroup` when the refreshed order details are needed.
     *
     * @param {Object} [options]
     * @param {boolean} [options.isBopis] - when true, sends the RopisCheckout property
     * @returns {Promise<void>}
     */
    createGooglePayPaymentGroup: async function ({ isBopis = false } = {}) {
        await checkoutApi.initializePaymentGroup({
            paymentGroupType: OrderUtils.PAYMENT_GROUP_TYPE.GOOGLE_PAY,
            // PascalCase `RopisCheckout` is what the cart-service BE expects on this
            // endpoint. The companion `initializeCheckout({ ropisCheckout })` call uses camelCase
            // because that helper transforms it to PascalCase internally (Checkout.js:392).
            ...(isBopis && { RopisCheckout: true })
        });
    },

    /**
     * Creates the Google Pay payment group and resolves with the refreshed
     * order details. Use `createGooglePayPaymentGroup` when the refresh is
     * unnecessary (e.g. the checkout paysheet flow, where shipping is already
     * configured and the caller does not consume the returned order details).
     *
     * @param {string|number} orderId - the checkout order id resolved from
     *   initializeCheckout
     * @param {Object} [options]
     * @param {boolean} [options.isBopis] - when true, sends the RopisCheckout property
     * @returns {Promise<Object>} resolved order details
     */
    initGooglePaymentGroup: async function (orderId, { isBopis = false, cartType = null } = {}) {
        await GooglePayUtils.createGooglePayPaymentGroup({ isBopis });

        // cartType (e.g. 'BOPIS') is required for Guest BOPIS orders so the BE returns the
        // correct order variant when reading against the 'current' anonymous order.
        return checkoutApi.getOrderDetails(orderId, '', false, false, '', cartType);
    },

    /**
     * Called after the Google Pay SDK script has loaded. Instantiates the
     * PaymentsClient and calls `isReadyToPay` to confirm the device/browser
     * supports Google Pay.
     *
     * @returns {Promise<void>} resolves when Google Pay is confirmed available
     * @throws {Error} if `isReadyToPay` returns a falsy result, indicating
     *   Google Pay is not supported on this device
     */
    onLibraryLoad: async function () {
        const client = this.getPaymentsClient();
        const response = await client.isReadyToPay(getIsReadyToPayRequest());

        if (!response?.result) {
            // TODO: GOOGLE — handle Google Pay unavailable on user's machine
            throw new Error('Google Pay is not available on this device');
        }
    },

    /**
     * Loads the SDK and verifies Google Pay is available on this device.
     * Memoizes the in-flight Promise so concurrent callers (e.g. desktop +
     * mobile ButtonGrid instances) share a single SDK load. On rejection
     * the cache is cleared so it can be retried.
     *
     * @returns {Promise<void>} resolves when the client is ready for
     *   startCheckout; rejects if the SDK can't load or the device is ineligible.
     */
    initPaymentsClient: function () {
        if (!this.initPromise) {
            this.initPromise = (async () => {
                try {
                    await this.loadLibrary();
                    await this.onLibraryLoad();
                } catch (err) {
                    this.initPromise = null;
                    this.loadLibraryPromise = null;
                    this.paymentsClient = null;
                    throw err;
                }
            })();
        }

        return this.initPromise;
    },

    /**
     * Loads the Google Pay JS SDK once (memoized). Rejects if the library
     * URL is missing from config or the script fails to load within
     * SDK_LOAD_TIMEOUT_MS — without these guards the Promise could hang
     * forever (LoadScripts has no onerror handler).
     *
     * @returns {Promise<void>} resolves when the script is loaded
     */
    loadLibrary: function () {
        if (!this.loadLibraryPromise) {
            const url = Sephora.configurationSettings.googlePayConfig?.googlePayLibrary;

            if (!url) {
                this.loadLibraryPromise = Promise.reject(new Error('Google Pay library URL not configured'));
            } else {
                this.loadLibraryPromise = new Promise((resolve, reject) => {
                    const timeoutId = setTimeout(() => reject(new Error('Google Pay SDK load timed out')), SDK_LOAD_TIMEOUT_MS);

                    LoadScripts.loadScripts(
                        [url],
                        () => {
                            clearTimeout(timeoutId);
                            resolve();
                        },
                        true
                    );
                });
            }
        }

        return this.loadLibraryPromise;
    },

    /**
     * Shared STH / SDD / Mixed shipping callback. Delegates the mid-sheet
     * SHIPPING_ADDRESS, SHIPPING_OPTION (STH only), and INITIALIZE triggers to
     * the shared dispatcher in GooglePayHandlers. The dispatcher branches on
     * `hasShippingOptions` (read from checkoutContext): strict `=== false`
     * takes the SDD/Mix path (recalc only); anything else, including
     * `true` (STH happy path) and `undefined` (no startCheckout has populated
     * checkoutContext yet), takes the STH path (recalc + shipping methods).
     *
     * @param {Object} intermediatePaymentData
     * @returns {Promise<Object>} PaymentDataRequestUpdate or PaymentDataError
     */
    onPaymentDataChanged: async function (intermediatePaymentData) {
        return GooglePayHandlers.onPaymentDataChanged(intermediatePaymentData, {
            orderId: checkoutContext?.orderId,
            shippingGroupId: checkoutContext?.shippingGroupId,
            shippingMethodIdsByGroup: checkoutContext?.shippingMethodIdsByGroup,
            hasShippingOptions: checkoutContext?.hasShippingOptions,
            transactionCurrencyCode: checkoutContext?.transactionCurrencyCode
        });
    },

    /**
     * STH authorization callback. Delegates to the shared handler which submits
     * the PaymentData to Cart Service and returns the SDK's
     * PaymentAuthorizationResult. The handler also stashes the placeOrder
     * response on `checkoutContext` so the post-paysheet redirect can read it
     * from `startCheckout`'s return value.
     *
     * @param {Object} paymentData
     * @returns {Promise<{ transactionState: string, error?: Object }>}
     */
    onSthPaymentAuthorized: async function (paymentData) {
        return GooglePayHandlers.onPaymentAuthorized(paymentData, checkoutContext);
    },

    /**
     * BOPIS paysheet authorization callback. Delegates to the shared
     * handler. The BOPIS `checkoutContext` omits `shippingGroupId`
     * and the paysheet omits `paymentData.shippingAddress`, so the shared
     * handler's address AND-guard skips the shipping-address call naturally;
     * the attach + placeOrder({ isExpressCheckout: true }) sequence still runs.
     * The pickup store is preserved because `initializeCheckout({ ropisCheckout: true })`
     * was passed at BOPIS init time — the backend keeps the pickup configuration
     * through submit.
     *
     * @param {Object} paymentData
     * @returns {Promise<{ transactionState: string, error?: Object }>}
     */
    onBopisPaymentAuthorized: async function (paymentData) {
        // Await background init so checkoutContext is populated before placeOrder.
        if (bopisBackgroundInitPromise) {
            const pending = bopisBackgroundInitPromise;

            bopisBackgroundInitPromise = null;
            await pending;
        }

        return GooglePayHandlers.onPaymentAuthorized(paymentData, checkoutContext);
    },

    /**
     * SDD / Mixed paysheet authorization callback. Delegates to the shared
     * handler. The SDD `checkoutContext` populates `shippingAddress` (from the
     * paysheet) plus `shippingGroupIds` + `shippingMethodIdsByGroup`,
     * so the shared handler's fan-outs attach the address and
     * method to every physical shipping group in a Mixed basket before the
     * attach + placeOrder({ isExpressCheckout: true }) sequence runs.
     *
     * @param {Object} paymentData
     * @returns {Promise<{ transactionState: string, error?: Object }>}
     */
    onSddPaymentAuthorized: async function (paymentData) {
        return GooglePayHandlers.onPaymentAuthorized(paymentData, checkoutContext);
    },

    /**
     * Checkout paysheet authorization callback. Delegates to the shared handler,
     * forwarding whatever `checkoutContext` the current in-flight flow populated.
     * For the checkout entrypoint (`openCheckoutPaysheet`), that context has no
     * shipping fields (shipping is already configured server-side), so the shared
     * handler skips shipping steps and goes straight to initializeGooglePayCheckout
     * + placeOrder. Called by the SDK via the client registered in
     * `getCheckoutPaymentsClient`.
     *
     * @param {Object} paymentData
     * @returns {Promise<{ transactionState: string, error?: Object }>}
     */
    onCheckoutPaymentAuthorized: async function (paymentData) {
        return GooglePayHandlers.onPaymentAuthorized(paymentData, checkoutContext);
    },

    /**
     * Opens the Google Pay paysheet from the checkout page. Unlike
     * `startCheckout` (basket express), this does NOT call
     * `initializeCheckout` because the order is already initialized and
     * shipping is already configured.
     *
     * Flow:
     * 1. Create/refresh the Google Pay payment group on the backend
     * 2. Build a checkout PaymentDataRequest (no shipping, FINAL price)
     * 3. Open the paysheet via `requestPaymentData`
     * 4. On approve → shared handler sends token + places order
     * 5. Return `{ googlePay, orderResponse }` for confirmation redirect
     *
     * @param {Object} options
     * @param {string} options.orderId - the already-initialized order id
     * @param {string} options.transactionAmount - formatted total (e.g. '99.99')
     * @param {string} options.transactionCurrencyCode - 'USD' or 'CAD'
     * @param {boolean} [options.isBopis] - when true, forwards `RopisCheckout: true`
     *   to the backend so the payment group is created for a store-pickup order
     * @returns {Promise<{ googlePay: { paymentData: Object }, orderResponse?: Object } | undefined>}
     *   Returns `undefined` if another paysheet open is already in flight.
     * @throws {Error} with `reason === 'incompleteCheckout'` when the user cancels.
     */
    openCheckoutPaysheet: async function ({ orderId, transactionAmount, transactionCurrencyCode, isBopis = false }) {
        // Both paysheet entrypoints share module-level `checkoutContext`, so guard
        // against the other flow being in flight to prevent overlapping sessions
        // where the SDK authorization callback would read the wrong context.
        if (openCheckoutPaysheetInFlight || startCheckoutInFlight) {
            return undefined;
        }

        openCheckoutPaysheetInFlight = true;

        try {
            checkoutContext = null;

            const googleClient = this.getCheckoutPaymentsClient();

            // Use the lean helper: on checkout, shipping is already configured and
            // the refreshed order details from initGooglePaymentGroup are unused.
            await this.createGooglePayPaymentGroup({ isBopis });

            checkoutContext = {
                orderId,
                isCheckout: true
            };

            const paymentDataRequest = getCheckoutPaymentDataRequest({
                transactionAmount,
                transactionCurrencyCode
            });

            const paysheetResult = await this.requestPaymentData(paymentDataRequest, googleClient);

            return {
                ...paysheetResult,
                orderResponse: checkoutContext?.orderResponse
            };
        } finally {
            checkoutContext = null;
            openCheckoutPaysheetInFlight = false;
        }
    },

    /**
     * Opens the Google Pay payment sheet with a prebuilt PaymentDataRequest and maps
     * the user-cancel outcome. Must be invoked AFTER `await initPaymentsClient()`.
     *
     * @param {Object} paymentDataRequest
     * @returns {Promise<{ googlePay: { paymentData: Object } }>}
     * @throws {Error} with `reason === 'incompleteCheckout'` when the user cancels.
     */
    requestPaymentData: async function (paymentDataRequest, googleClient) {
        const client = googleClient || this.getPaymentsClient();

        try {
            const paymentData = await client.loadPaymentData(paymentDataRequest);

            return { googlePay: { paymentData } };
        } catch (err) {
            // Google Pay returns this statusCode when the user closes the sheet
            if (err?.statusCode === SDK_STATUS_CODES.CANCELED) {
                const error = new Error('User cancelled Google Pay checkout');

                error.reason = GOOGLE_PAY_ERROR_MESSAGES.incompleteCheckout;

                throw error;
            }

            throw err;
        }
    },

    /**
     * Runs the express-checkout init sequence (initializeCheckout,
     * initGooglePaymentGroup, getAvailableShippingMethods for STH), populates the
     * module-level `checkoutContext` needed by the shared onPaymentAuthorized
     * handler, and builds the appropriate `PaymentDataRequest`.
     *
     * Shared by `startCheckout` (legacy classic single-tap) and
     * `runPendingCheckoutInit` (placeholder-first — runs from the paysheet's
     * INITIALIZE callback with the tap-time params).
     * Does NOT manage in-flight guards or the interstitial spinner — callers
     * own that state. Does NOT call `loadPaymentData` — callers open the
     * paysheet themselves.
     *
     * @private
     * @throws re-throws init errors so the caller can log / restore UI state.
     * @returns {Promise<{ paymentDataRequest: Object, googleClient: Object }>}
     */
    _prepareCheckoutSession: async function ({ transactionAmount, transactionCurrencyCode, isBopis, isSdd }) {
        checkoutContext = null;

        let googleClient;

        if (isBopis) {
            googleClient = this.getBopisPaymentsClient();
        } else if (isSdd) {
            googleClient = this.getSddPaymentsClient();
        } else {
            googleClient = this.getPaymentsClient();
        }

        // Guests check out anonymously so the express paysheet opens without a
        // sign-in gate — mirrors the Apple Pay guest express flow. The anonymous
        // init hits /api/checkout/initAnonymousPurchase (un-gated) instead of
        // /api/checkout/order/init (which triggers the sign-in popup). Only when
        // guest checkout is enabled for this cart type; otherwise fall back to the
        // signed-in init, which still prompts sign-in.
        const state = store.getState();
        const isGuestCheckoutEnabled = isBopis ? isGuestCheckoutEnabledForBopisSelector(state) : isGuestCheckoutEnabledSelector(state);
        const useGuestCheckout = !userUtils.isSignedIn() && isGuestCheckoutEnabled;

        const order = await checkoutUtils.initializeCheckout({
            isGooglePayFlow: true,
            isGuestCheckout: useGuestCheckout,
            ropisCheckout: isBopis
        });

        const orderId = useGuestCheckout ? 'current' : order?.orderId || order?.currentOrderId;
        const cartType = checkoutUtils.getLastInitCartType(orderId, useGuestCheckout && isBopis);

        const orderDetails = await this.initGooglePaymentGroup(orderId, { isBopis, cartType });

        let shippingMethods = [];

        if (isBopis) {
            checkoutContext = {
                orderId,
                priceInfo: orderDetails?.priceInfo,
                isBopis: true,
                // Guest BOPIS has no shipping address to carry the pickup email, so the
                // auth handler must POST pickup details before submit. Signed-in orders
                // already have the pickup contact from the profile.
                isGuestCheckout: useGuestCheckout
            };
        } else if (isSdd) {
            const shippingGroup = OrderUtils.getSameDayShippingGroup(orderDetails);

            if (!shippingGroup) {
                throw new Error('Unsupported cart type: missing SameDayShippingGroup');
            }

            checkoutContext = {
                orderId,
                shippingGroupId: shippingGroup.shippingGroupId,
                shippingGroupIds: OrderUtils.getPhysicalAddressShippingGroupIds(orderDetails),
                shippingMethodIdsByGroup: OrderUtils.getShippingMethodIdsByPhysicalGroup(orderDetails),
                priceInfo: orderDetails?.priceInfo,
                transactionCurrencyCode,
                hasShippingOptions: false,
                isSdd: true
            };
        } else {
            // Gift-card-only baskets have no HardGoodShippingGroup, only a
            // GiftCardShippingGroup. Fall back to the GC group so express
            // checkout works for a physical-GC-only basket (PAY-7637).
            const shippingGroup = OrderUtils.getHardGoodShippingGroup(orderDetails) || OrderUtils.getPhysicalGiftCardShippingGroup(orderDetails);

            if (!shippingGroup) {
                throw new Error('Unsupported cart type: missing HardGoodShippingGroup or GiftCardShippingGroup');
            }

            const shippingGroupId = shippingGroup.shippingGroupId;
            const currentShippingMethodId = shippingGroup.shippingMethod?.shippingMethodId;

            const response = await checkoutApi.getAvailableShippingMethods(orderId, shippingGroupId);
            shippingMethods = response?.shippingMethods || [];

            checkoutContext = {
                orderId,
                shippingGroupId,
                shippingGroupIds: OrderUtils.getPhysicalAddressShippingGroupIds(orderDetails),
                shippingMethodIdsByGroup: OrderUtils.getShippingMethodIdsByPhysicalGroup(orderDetails),
                shippingMethods,
                priceInfo: orderDetails?.priceInfo,
                currentShippingMethodId,
                transactionCurrencyCode,
                hasShippingOptions: true,
                isBopis: false
            };
        }

        // The button passes the merchandise subtotal (pre-tax); the paysheet
        // must show the same tax/fee-inclusive total as checkout, so resolve it
        // from the initialized order's priceInfo.orderTotal.
        const resolvedAmount = resolveTransactionAmount(orderDetails?.priceInfo, transactionAmount);

        let paymentDataRequest;

        if (isBopis) {
            paymentDataRequest = getBopisPaymentDataRequest({ transactionAmount: resolvedAmount, transactionCurrencyCode });
        } else if (isSdd) {
            paymentDataRequest = getSddPaymentDataRequest({ transactionAmount: resolvedAmount, transactionCurrencyCode });
        } else {
            paymentDataRequest = getSthPaymentDataRequest({
                transactionAmount: resolvedAmount,
                transactionCurrencyCode,
                shippingMethods,
                currentShippingMethodId: checkoutContext.currentShippingMethodId
            });
        }

        return { paymentDataRequest, googleClient };
    },

    /**
     * Placeholder-first entry.
     *
     * Called synchronously from the tap handler so that `loadPaymentData`
     * lands in the same run-to-completion cycle as the click. This preserves
     * transient user-activation across the paysheet dispatch (WebKit revokes
     * it across any `await`, Chrome revokes it on the second
     * `PaymentRequest.show()` per page load), so the classic
     * `startCheckout` — which awaits backend init before `loadPaymentData` —
     * fails on iOS Safari every tap and on Chrome on the second tap.
     *
     * This method:
     *   1. Stashes the real init params on `pendingInit`.
     *   2. Builds a placeholder PaymentDataRequest ($0.00 total + placeholder
     *      shipping option, no backend round-trip).
     *   3. Delegates to `requestPaymentData(placeholder, client)` and
     *      awaits its returned Promise inside an async IIFE. `requestPaymentData`
     *      is an async fn whose body runs synchronously up to its first
     *      `await`, so `client.loadPaymentData(placeholder)` still fires in
     *      the same run-to-completion tick as the click — NO awaits before
     *      the SDK call, so WebKit keeps the user-activation flag alive and
     *      the paysheet opens with Google's native loading UI.
     *
     * The paysheet's `INITIALIZE` callback (see GooglePayHandlers.handleInitialize)
     * detects the stashed `pendingInit`, runs the same init as `startCheckout`
     * (initializeCheckout + initGooglePaymentGroup + getAvailableShippingMethods
     * for STH) inside the paysheet's own loading window, and returns
     * `newTransactionInfo` + `newShippingOptionParameters` to update the paysheet
     * with the real cart data.
     *
     * NOT an async function on purpose — must be callable in the same JS
     * run-to-completion cycle as the click event. Returns a Promise for the
     * paysheet result.
     */
    openPaysheetInitOnDemand: function ({ transactionAmount, transactionCurrencyCode, isBopis = false, isSdd = false }) {
        if (startCheckoutInFlight || openCheckoutPaysheetInFlight) {
            return Promise.resolve(undefined);
        }

        startCheckoutInFlight = true;
        checkoutContext = null;
        setPendingInit({ transactionAmount, transactionCurrencyCode, isBopis, isSdd });

        let googleClient;
        let placeholderRequest;

        // `get*PaymentsClient` calls `createPaymentsClient()` which throws when
        // the SDK script hasn't loaded. Without this guard, the throw would
        // bypass the `.finally` below and leave `startCheckoutInFlight`
        // permanently true — every subsequent tap would silently no-op until
        // page refresh.
        try {
            if (isBopis) {
                googleClient = this.getBopisPaymentsClient();
            } else if (isSdd) {
                googleClient = this.getSddPaymentsClient();
            } else {
                googleClient = this.getPaymentsClient();
            }

            placeholderRequest = buildPlaceholderPaymentDataRequest({ transactionAmount, transactionCurrencyCode, isBopis, isSdd });
        } catch (err) {
            startCheckoutInFlight = false;
            clearPendingInit();

            return Promise.reject(err);
        }

        // Delegate to `requestPaymentData` so the cancel/error normalization
        // stays consistent with the classic single-tap flow. Safe re the
        // WebKit user-activation constraint: the async IIFE below starts
        // executing synchronously up to its first `await`, so the call to
        // `this.requestPaymentData(...)` — and inside it, its own sync-until-
        // await path to `client.loadPaymentData(placeholderRequest)` — still
        // fires inside the fresh user gesture. That is why the outer entry
        // is a plain function that returns a Promise instead of being `async`
        // itself: nothing here awaits before that dispatch.
        const requestPromise = this.requestPaymentData(placeholderRequest, googleClient);

        // BOPIS has no INITIALIZE callback, so run backend init in the background.
        // Kicked off after loadPaymentData dispatches to preserve user-activation.
        if (isBopis) {
            bopisBackgroundInitPromise = this.runPendingCheckoutInit().catch(err => {
                Sephora?.logger?.error('[GooglePay] BOPIS background init failed', err);
            });
        }

        return (async () => {
            try {
                const { googlePay } = await requestPromise;

                return { googlePay, orderResponse: checkoutContext?.orderResponse };
            } finally {
                startCheckoutInFlight = false;
                clearPendingInit();

                // On paysheet cancel, onBopisPaymentAuthorized never runs and would
                // leave the settled promise pinned on the module.
                if (isBopis) {
                    bopisBackgroundInitPromise = null;
                }
            }
        })();
    },

    /**
     * Runs the same init sequence as `startCheckout` but sourced from the
     * `pendingInit` state stashed by `openPaysheetInitOnDemand`. Populates the
     * module-level `checkoutContext` and returns the pieces the INITIALIZE
     * callback needs to update the paysheet.
     *
     * @returns {Promise<{ priceInfo: Object, shippingMethods: Array, orderId?: string, shippingGroupId?: string, hasShippingOptions?: boolean, transactionCurrencyCode: string, isBopis: boolean, isSdd: boolean }>}
     */
    runPendingCheckoutInit: async function () {
        const pending = getPendingInit();

        if (!pending) {
            throw new Error('runPendingCheckoutInit called with no pendingInit');
        }

        // Consume-once: Google Pay fires `INITIALIZE` again on subsequent
        // address / shipping-option changes within the same paysheet session.
        // Clearing here makes the handler fall through to its normal address
        // path on those repeat callbacks instead of re-running the full
        // backend init chain.
        clearPendingInit();

        const { transactionAmount, transactionCurrencyCode, isBopis, isSdd } = pending;
        const prepared = await this._prepareCheckoutSession({
            transactionAmount,
            transactionCurrencyCode,
            isBopis,
            isSdd
        });

        // `_prepareCheckoutSession` returns `{ paymentDataRequest, googleClient }`,
        // but INITIALIZE needs the raw priceInfo + methods to build the callback
        // response payload. Re-read them from the just-populated checkoutContext.
        return {
            priceInfo: checkoutContext?.priceInfo,
            shippingMethods: checkoutContext?.shippingMethods || [],
            currentShippingMethodId: checkoutContext?.currentShippingMethodId,
            // Exposed so handleInitialize can align the paysheet total with the
            // wallet's default address. Absent for BOPIS.
            orderId: checkoutContext?.orderId,
            shippingGroupId: checkoutContext?.shippingGroupId,
            hasShippingOptions: checkoutContext?.hasShippingOptions,
            transactionCurrencyCode,
            isBopis,
            isSdd,
            // Expose paymentDataRequest for tests / debug; INITIALIZE doesn't use it.
            _preparedRequest: prepared.paymentDataRequest
        };
    },

    /**
     * Runs the express-checkout init sequence, then opens the paysheet.
     *
     * STH:   order/init → paymentGroup (GooglePayPaymentGroup) → getOrderDetails →
     *        getAvailableShippingMethods → STH PaymentDataRequest → loadPaymentData.
     * BOPIS: order/init (RopisCheckout) → paymentGroup (GooglePayPaymentGroup,
     *        RopisCheckout) → getOrderDetails → BOPIS PaymentDataRequest →
     *        loadPaymentData. Skips the shipping-methods lookup (no shipping for
     *        pickup).
     * SDD:   order/init → paymentGroup (GooglePayPaymentGroup) → getOrderDetails →
     *        SDD PaymentDataRequest → loadPaymentData. Skips the shipping-methods
     *        lookup (paysheet has no method selector for SDD/Mix; the cart's
     *        fulfillment composition fixes delivery). Used for SDD-only AND
     *        Mixed (STH+SDD) baskets.
     *
     * Populates `checkoutContext`, reserved for callback bodies.
     * On any init failure the order-failure flow runs and the sheet is NOT opened.
     *
     * @param {Object} args
     * @param {string} args.transactionAmount - amount in major units (e.g. '10.00')
     * @param {string} args.transactionCurrencyCode - ISO 4217 code (e.g. 'USD', 'CAD')
     * @param {boolean} [args.isBopis] - true when the basket is a BOPIS pickup cart
     * @param {boolean} [args.isSdd] - true when the basket has SDD items (SDD-only or
     *   Mixed STH+SDD). Ignored when `isBopis` is true.
     * @returns {Promise<{ googlePay: { paymentData: Object }, orderResponse?: Object } | undefined>}
     *   On success, `orderResponse` carries the placeOrder result stashed on
     *   checkoutContext by the shared onPaymentAuthorized handler — the caller
     *   passes it to `checkoutUtils.placeOrderSuccess` for the confirmation redirect.
     * @throws {Error} with `reason === 'incompleteCheckout'` when the user cancels.
     */
    startCheckout: async function ({ transactionAmount, transactionCurrencyCode, isBopis = false, isSdd = false }) {
        // Both paysheet entrypoints share module-level `checkoutContext`, so guard
        // against the other flow being in flight to prevent overlapping sessions
        // where the SDK authorization callback would read the wrong context.
        if (startCheckoutInFlight || openCheckoutPaysheetInFlight) {
            return undefined;
        }

        startCheckoutInFlight = true;

        // Identifies a resumable session: same cart + cart type + amount + currency.
        // Note: nullish-coalesce so JSON.stringify doesn't drop the key when orderId is missing.
        const basketOrderId = store.getState()?.basket?.orderId ?? null;
        const reuseKey = JSON.stringify({
            basketOrderId,
            transactionAmount,
            transactionCurrencyCode,
            isBopis,
            isSdd
        });
        try {
            // Reopen-after-close fast path. When the user closed the paysheet and
            // clicks again with an unchanged cart, the prior session's backend init
            // is still valid (checkoutContext survives a cancel — it is only nulled
            // on the next full init below). Skipping the init awaits keeps the
            // click's transient user activation alive, so this non-first
            // PaymentRequest.show() is not rejected by Chrome. loadPaymentData is
            // the first and only await on this path.
            if (checkoutContext?.orderId && !checkoutContext.orderResponse && checkoutContext.reuseKey === reuseKey) {
                const reuseClient = this.getExpressPaymentsClient({ isBopis, isSdd });
                const reuseRequest = buildExpressPaymentDataRequest({
                    isBopis,
                    isSdd,
                    transactionAmount: resolveTransactionAmount(checkoutContext.priceInfo, transactionAmount),
                    transactionCurrencyCode,
                    shippingMethods: checkoutContext.shippingMethods,
                    currentShippingMethodId: checkoutContext.currentShippingMethodId
                });

                const reuseResult = await this.requestPaymentData(reuseRequest, reuseClient);

                return {
                    ...reuseResult,
                    orderResponse: checkoutContext?.orderResponse
                };
            }

            // Show the loading interstitial while init awaits, which prevents
            // double clicking. Hidden right before requestPaymentData opens the sheet.
            store.dispatch(Actions.showInterstice(true));

            let prepared;

            try {
                prepared = await this._prepareCheckoutSession({ transactionAmount, transactionCurrencyCode, isBopis, isSdd });
            } catch (err) {
                Sephora.logger.error('[GooglePay] checkout init failed', err);
                checkoutUtils.initOrderFailure(err);
                store.dispatch(Actions.showInterstice(false));

                return undefined;
            }

            // Tag the freshly built session so a later reopen-after-close click can
            // resume it via the fast path above instead of re-running init.
            checkoutContext.reuseKey = reuseKey;

            // Hide the interstitial synchronously right before requestPaymentData
            // fires loadPaymentData  so the spinner disappears
            // just as the paysheet backdrop appears.
            // If requestPaymentData throws (cancel or hard error), the spinner
            // is already hidden; the button's catch handles the rest.
            store.dispatch(Actions.showInterstice(false));

            const paysheetResult = await this.requestPaymentData(prepared.paymentDataRequest, prepared.googleClient);

            return {
                ...paysheetResult,
                orderResponse: checkoutContext?.orderResponse
            };
        } finally {
            startCheckoutInFlight = false;
        }
    }
};

export default GooglePayUtils;
